import {Button, Dialog, toast} from '@wandb/components';
import React, {useEffect, useState} from 'react';

import {useDispatch, useMemoizedSelector} from '../../state/hooks';
import {
  discardWorkspaceSaveConflict,
  overwriteWorkspaceSaveConflict,
} from '../../state/workspaces/actions';
import {saveAsNewWorkspace} from '../../state/workspaces/namedWorkspacesActions';
import {makeWorkspaceSaveConflictSelector} from '../../state/workspaces/selectors';
import {type ThunkResult} from '../../types/redux';
import {useSemanticLegendSettingsContext} from '../WorkspaceDrawer/Settings/semanticLegend/SemanticLegendSettingsWithCategoricalConfigContextProvider';

type Resolution = 'overwrite' | 'save-as-new' | 'discard';

type WorkspaceSaveConflictModalProps = {
  workspaceID: string;
};

/**
 * Shown when a save (autosave or "Save view") was rejected because the
 * workspace changed on the server, for example by ARIA. Saving stays paused
 * until the user picks which version to keep, so neither version is lost
 * silently.
 */
export const WorkspaceSaveConflictModal = ({
  workspaceID,
}: WorkspaceSaveConflictModalProps) => {
  const dispatch = useDispatch();
  const conflict = useMemoizedSelector(
    makeWorkspaceSaveConflictSelector,
    workspaceID
  );
  const {semanticLegendSettings, semanticLegendRunColorOptions} =
    useSemanticLegendSettingsContext();
  const [pending, setPending] = useState<Resolution | null>(null);

  // With no local edits there is nothing to choose between, so adopt the
  // server version without asking. This happens when a tab's background
  // save (panel init, migrations) races a save from another tab or ARIA.
  useEffect(() => {
    if (conflict !== 'no-local-edits') {
      return;
    }
    dispatch(discardWorkspaceSaveConflict(workspaceID)).catch(err => {
      console.error('Failed to reload workspace after save conflict', err);
      toast.error(
        "Couldn't load the latest version of this workspace. Please refresh."
      );
    });
  }, [conflict, dispatch, workspaceID]);

  const resolve = async (
    resolution: Resolution,
    action: ThunkResult<unknown>
  ) => {
    setPending(resolution);
    try {
      await dispatch(action);
    } catch (err) {
      console.error('Failed to resolve workspace save conflict', err);
      toast.error("Couldn't resolve the conflict. Please try again.");
    } finally {
      setPending(null);
    }
  };

  return (
    // Escape and outside clicks are ignored: the conflict stays until the
    // user chooses a resolution.
    <Dialog.Modal
      isAlert
      isOpen={conflict === 'local-edits'}
      isDismissable={false}>
      <Dialog.Heading>This workspace was changed elsewhere</Dialog.Heading>
      <Dialog.Content>
        <p>
          A newer version of this workspace was saved, for example by ARIA or
          another tab, before your changes were saved. Choose which version to
          keep.
        </p>
      </Dialog.Content>
      <Dialog.Footer>
        <Button
          isPending={pending === 'overwrite'}
          isDisabled={pending != null}
          onPress={() =>
            resolve('overwrite', overwriteWorkspaceSaveConflict(workspaceID))
          }>
          Keep my changes
        </Button>
        <Button
          variant="secondary"
          isPending={pending === 'save-as-new'}
          isDisabled={pending != null}
          onPress={() =>
            resolve(
              'save-as-new',
              saveAsNewWorkspace(workspaceID, 'team', {
                ...semanticLegendSettings,
                runColorOptions: semanticLegendRunColorOptions,
              })
            )
          }>
          Save mine as a new view
        </Button>
        <Button
          variant="secondary"
          isPending={pending === 'discard'}
          isDisabled={pending != null}
          onPress={() =>
            resolve('discard', discardWorkspaceSaveConflict(workspaceID))
          }>
          Discard my changes
        </Button>
      </Dialog.Footer>
    </Dialog.Modal>
  );
};
